import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { getSelectorAttribute, navigationSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import { first, isArray, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import React, { useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { renderWithPermissions } from '../../../../services/render-with-permissions';
import { BaseSideNavBarItem, NavBarItem } from '../side-nav-bar-types/side-nav-bar-item';
import { SIDE_NAV_BAR_ITEMS } from '../side-nav-bar-types/side-nav-bar-items';
import { SIDE_NAV_BAR_LABELS } from '../side-nav-bar-types/side-nav-bar-labels';
import { SideNavBarRoute } from '../side-nav-bar-types/side-nav-bar-routes';

const NAV_ITEM_SELECTORS: Record<string, string> = {
  [SIDE_NAV_BAR_LABELS.Home]: navigationSelectors.homeLink,
  [SIDE_NAV_BAR_LABELS.Customers]: navigationSelectors.customersLink,
  [SIDE_NAV_BAR_LABELS.Invoices]: navigationSelectors.invoicesLink,
  [SIDE_NAV_BAR_LABELS.Plans]: navigationSelectors.plansLink,
  [SIDE_NAV_BAR_LABELS.DataMetering]: navigationSelectors.usageLink,
  [SIDE_NAV_BAR_LABELS.Reports]: navigationSelectors.reportsLink,
  [SIDE_NAV_BAR_LABELS.Products]: navigationSelectors.productsLink,
  [SIDE_NAV_BAR_LABELS.Integrations]: navigationSelectors.integrationsLink,
};

const NAV_SUB_ITEM_SELECTORS: Record<string, string> = {
  [SIDE_NAV_BAR_LABELS.Overview]: navigationSelectors.invoicesOverviewLink,
  [SIDE_NAV_BAR_LABELS.Payments]: navigationSelectors.invoicesPaymentsLink,
  [SIDE_NAV_BAR_LABELS.Usage]: navigationSelectors.dataMeteringUsageLink,
  [SIDE_NAV_BAR_LABELS.Events]: navigationSelectors.dataMeteringEventsLink,
};

type NavItemListProps = {
  isDrawerOpen: boolean;
  isSubmenuOpen: (itemText: string) => boolean;
  handleSubMenuClick: (itemText: string) => void;
};

export const NavItemList: React.FC<NavItemListProps> = ({
  isDrawerOpen,
  handleSubMenuClick,
  isSubmenuOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isSubItemSelected = useCallback(
    (subItem: BaseSideNavBarItem) => {
      const currentPath = location.pathname;
      const subItemRoute = isArray(subItem.to) ? first(subItem.to) : subItem.to;

      const isNaturallySelected = isArray(subItem.to)
        ? subItem.to.some((path: string) => currentPath.startsWith(path))
        : currentPath.startsWith(subItem.to);

      const shouldInvoiceOverviewBeSelected =
        subItemRoute === '/invoices/overview' &&
        currentPath.startsWith('/invoices/') &&
        !currentPath.startsWith('/invoices/payments');

      return isNaturallySelected || shouldInvoiceOverviewBeSelected;
    },
    [location.pathname],
  );

  const handleNavItemClick = useCallback(
    (event: React.MouseEvent, item: NavBarItem) => {
      event.preventDefault();

      if (isNotNullOrUndefined(item.subItems)) {
        if (isDrawerOpen) {
          handleSubMenuClick(item.text);
          return;
        }

        const [subItem] = item.subItems;

        const path = resolvePath(subItem.to);

        if (isNullOrUndefined(path)) {
          return;
        }

        navigate(path);
        return;
      }

      const targetPath = resolvePath(item.to);

      if (!targetPath) {
        return;
      }

      const isSamePath = targetPath === location.pathname;
      const shouldPreserveSearch = isSamePath && !item.clearSearchOnNavigate;

      const newPath = shouldPreserveSearch ? targetPath + location.search : targetPath;

      navigate(newPath);
    },
    [handleSubMenuClick, location.search, location.pathname, navigate],
  );

  const handleSubItemClick = useCallback(
    (event: React.MouseEvent, subItem: BaseSideNavBarItem) => {
      event.preventDefault();
      const targetPath = isArray(subItem.to) ? first(subItem.to) : subItem.to;
      navigate(targetPath + location.search);
    },
    [navigate, location.search],
  );

  return (
    <List
      component="nav"
      sx={{
        gap: '20px',
        pt: '20px',
        padding: '0px 24px',
        alignSelf: 'stretch',
        flex: 1,
      }}
    >
      {SIDE_NAV_BAR_ITEMS.map((item) =>
        renderWithPermissions(item.permission, (hasPermission) => {
          if (!hasPermission) {
            return null;
          }

          const hasSubItems = isNotNullOrUndefined(item.subItems);
          const isSelected = isArray(item.to)
            ? item.to.some((path) => location.pathname.startsWith(path))
            : location.pathname.startsWith(item.to);

          return (
            <React.Fragment key={item.text}>
              <ListItemButton
                component={hasPermission && isNotNullOrUndefined(item.to) ? NavLink : 'div'}
                onClick={(event: React.MouseEvent) => handleNavItemClick(event, item)}
                disabled={!hasPermission}
                {...(NAV_ITEM_SELECTORS[item.text]
                  ? getSelectorAttribute(NAV_ITEM_SELECTORS[item.text])
                  : {})}
                sx={{
                  padding: '12px',
                  alignItems: 'center',
                  gap: '10px',
                  alignSelf: 'stretch',
                  position: 'relative',
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: isSelected ? '#263C6E' : 'transparent',
                  '&:hover': {
                    bgcolor: hasPermission ? '#1B2F5C' : 'transparent',
                  },
                  height: 48,
                  opacity: hasPermission ? 1 : 0.3,
                  cursor: hasPermission ? 'pointer' : 'not-allowed',
                }}
              >
                <ListItemIcon
                  title={!isDrawerOpen ? item.text : undefined}
                  sx={{
                    minWidth: 'auto',
                    justifyContent: 'center',
                    color: isSelected ? '#43FFF1' : '#D6DBE7',
                  }}
                >
                  {/*
                    Both branches inherit color from ListItemIcon's sx.color
                    above (cascades through CSS into SVG `currentColor`). No
                    explicit `color` prop needed on the icon.
                  */}
                  {typeof item.icon === 'string' ? (
                    <VyIcon icon={item.icon} strokeOpacity={1} />
                  ) : (
                    <item.icon width={24} height={24} />
                  )}
                </ListItemIcon>

                <ListItemText
                  primary={item.text}
                  sx={{
                    position: 'absolute',
                    left: '56px',
                    opacity: isDrawerOpen ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: isDrawerOpen ? 'auto' : 'none',
                    color: isSelected ? '#43FFF1' : '#D6DBE7',
                    '& .MuiListItemText-primary': {
                      fontSize: 16,
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </ListItemButton>

              {hasSubItems && (
                <Collapse in={isSubmenuOpen(item.text)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ paddingLeft: isDrawerOpen ? 5 : 0, my: 1 }}>
                    {item.subItems?.map((subItem) =>
                      renderWithPermissions(subItem.permission, (subHasPermission) => {
                        const isActive = isSubItemSelected(subItem);

                        return (
                          <ListItemButton
                            component={subHasPermission ? NavLink : 'div'}
                            key={subItem.text}
                            to={subHasPermission ? subItem.to : '#'}
                            {...(NAV_SUB_ITEM_SELECTORS[subItem.text]
                              ? getSelectorAttribute(NAV_SUB_ITEM_SELECTORS[subItem.text])
                              : {})}
                            onClick={(event: React.MouseEvent) => {
                              if (subHasPermission) {
                                handleSubItemClick(event, subItem);
                              }
                            }}
                            disabled={!subHasPermission}
                            sx={{
                              mx: -1,
                              borderLeft: `1px solid #186873`,
                              position: 'relative',
                              '& .MuiListItemText-primary': {
                                fontWeight: isActive ? 700 : 400,
                              },
                              '&::before': isActive
                                ? {
                                    content: '""',
                                    position: 'absolute',
                                    left: -5,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: '#43FFF1',
                                  }
                                : {},
                              '&.active .MuiListItemText-primary': {
                                fontWeight: 700,
                              },
                              '&.active::before': {
                                content: '""',
                                position: 'absolute',
                                left: -5,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: '#43FFF1',
                              },
                              opacity: subHasPermission ? 1 : 0.3,
                              cursor: subHasPermission ? 'pointer' : 'not-allowed',
                            }}
                          >
                            <ListItemText
                              primary={subItem.text}
                              sx={{
                                opacity: isDrawerOpen ? 1 : 0,
                                transition: 'opacity 0.2s ease',
                                pointerEvents: isDrawerOpen ? 'auto' : 'none',
                                color: '#43FFF1',
                                '& .MuiListItemText-primary': {
                                  fontSize: 14,
                                  fontWeight: 400,
                                  whiteSpace: 'nowrap',
                                },
                              }}
                            />
                          </ListItemButton>
                        );
                      }),
                    )}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        }),
      )}
    </List>
  );
};

function resolvePath(path: SideNavBarRoute | SideNavBarRoute[]) {
  return isArray(path) ? first(path) : path;
}
